import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

export interface SegmentedToggleOption<TValue extends string> {
  value: TValue;
  label: ReactNode;
  count?: number;
  disabled?: boolean;
}

export interface SegmentedToggleProps<TValue extends string> {
  value: TValue;
  options: SegmentedToggleOption<TValue>[];
  onChange: (value: TValue) => void;
  ariaLabel?: string;
}

export function SegmentedToggle<TValue extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: SegmentedToggleProps<TValue>) {
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      onChange={(_, next: TValue | null) => {
        if (next !== null) {
          onChange(next);
        }
      }}
      aria-label={ariaLabel}
      sx={(theme) => ({
        width: "fit-content",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        padding: 0,
        gap: 0,
        overflow: "hidden",
      })}
    >
      {options.map((opt, idx) => {
        const isOn = opt.value === value;
        const isLast = idx === options.length - 1;
        return (
          <ToggleButton
            key={opt.value}
            value={opt.value}
            disabled={opt.disabled}
            sx={(theme) => ({
              padding: 1,
              borderRadius: 0,
              borderRight: isLast
                ? "none"
                : `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.primary,
              "&.Mui-selected": {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary._states.selected,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: theme.palette.primary._states.selected,
                },
              },
            })}
          >
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 700 }}
            >
              {opt.label}
            </Typography>
            {opt.count !== undefined && (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 0.75,
                  borderRadius: 9999,
                  bgcolor: isOn ? "primary._states.selected" : "action.hover",
                  color: isOn ? "primary.main" : "text.secondary",
                }}
              >
                <Typography component="span" variant="body4">
                  {opt.count.toLocaleString()}
                </Typography>
              </Box>
            )}
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
}
